// Translation keys for Index Details page
export type TranslationKey =
  | "indexDescription"
  | "performance"
  | "from"
  | "to"
  | "top10Constituents"
  | "downloadMaterials"
  | "utcDisclaimer"
  | "ytd"
  | "year1"
  | "year3"
  | "year5"
  | "max"
  | "indexSymbol"
  | "currency"
  | "figi"
  | "isin"
  | "nConstituents"
  | "inceptionDate"
  | "value"
  | "date"
  | "datePlaceholder"
  | "lastDayReturn"
  | "name"
  | "ticker"
  | "indexShares"
  | "indexReturns"
  | "indexVolatility"
  | "return"
  | "annualizedReturn"
  | "sharpeRatio"
  | "dailyVolatility"
  | "annualizedVolatility"
  | "maxDrawdown"
  | "indexResources"
  | "factsheet"
  | "methodologyGuidebook"
  | "constituentsList"
  | "corporateActions"
  | "benchmarkStatement"
  | "indexHistory"
  | "rebalanceConstituents"
  | "headerHome"
  | "headerIndexing"
  | "headerBitaIndexes"
  | "headerIndexServices"
  | "headerBitacore"
  | "headerThematics"
  | "headerEsgImpact"
  | "headerIndexingApis"
  | "headerMore"
  | "headerGovernance"
  | "headerAnnouncements"
  | "headerBlog"
  | "headerContactUs"
  | "footerContactUsTitle"
  | "footerContactUsDescription"
  | "footerFollowUs"
  | "footerCopyright"
  | "footerCompany"
  | "footerAboutBita"
  | "footerOurClients"
  | "footerGovernance"
  | "footerCareers"
  | "footerIndexing"
  | "footerBitaIndexes"
  | "footerIndexLab"
  | "footerBitacore"
  | "footerIndexServices"
  | "footerIndexingApis"
  | "footerCryptoOffering"
  | "footerIndexAnnouncements"
  | "footerData"
  | "footerThematics"
  | "footerEsgImpact"
  | "footerConnect"
  | "footerBlog"
  | "footerLinkedin"
  | "footerTwitter"
  | "footerContactUs"
  | "footerNewsletter"
  | "footerLegalPrivacy"
  | "footerCookiesPolicy"
  | "footerTermsConditions"
  | "footerDisclaimer"
  | "footerImprint"
  | "footerPrivacyPolicy"
  | "footerLicenseIndex"
  | "footerEnhanceProduct"
  | "footerLicenseBestInClass"
  | "footerUnlockPotential"
  | "footerGetStarted"
  | "footerContactUsTech"
  | "footerReadyToGetStarted"
  | "footerLicenseIndexDescription"
  | "footerEnhanceProductDescription"
  | "footerLicenseBestInClassDescription"
  | "footerUnlockPotentialDescription"
  | "footerGetStartedDescription"
  | "footerContactUsTechDescription"
  | "footerReadyToGetStartedDescription"
  | "breadcrumbBitaIndexes"
  | "breadcrumbEquity"
  | "breadcrumbFixedIncome"
  | "breadcrumbCommodities"
  | "breadcrumbThematic"
  | "breadcrumbSector"
  | "breadcrumbESG";

export type LanguageCode = "en" | "fr";

// ISO 639-1 language code constants
export const ISO_CODE_EN: LanguageCode = "en";
export const ISO_CODE_FR: LanguageCode = "fr";

// Date format configuration based on locale
// French uses DD/MM/YYYY, English uses YYYY/MM/DD
export const DATE_FORMATS: Record<LanguageCode, string> = {
  [ISO_CODE_FR]: "DD/MM/YYYY",
  [ISO_CODE_EN]: "YYYY/MM/DD",
};

export interface Translations {
  [key: string]: string | Translations;
}

// English translations (default)
const enTranslations: Record<TranslationKey, string> = {
  indexDescription: "Index Description",
  performance: "Performance",
  from: "From",
  to: "To",
  top10Constituents: "Top 10 Constituents",
  downloadMaterials: "Download Materials",
  utcDisclaimer: "*Please note that the graph utilizes UTC time instead of local time.",
  ytd: "YTD",
  year1: "1 Year",
  year3: "3 Years",
  year5: "5 Years",
  max: "Max.",
  indexSymbol: "Index Symbol",
  currency: "Currency",
  figi: "FIGI",
  isin: "ISIN",
  nConstituents: "N° of Constituents",
  inceptionDate: "Inception Date",
  value: "Value",
  date: "Date",
  datePlaceholder: "yyyy-mm-dd",
  lastDayReturn: "Last Day Return",
  name: "NAME",
  ticker: "TICKER",
  indexShares: "INDEX SHARES",
  indexReturns: "Index Returns",
  indexVolatility: "Index Volatility",
  return: "Return",
  annualizedReturn: "Annualized Return",
  sharpeRatio: "Sharpe Ratio",
  dailyVolatility: "Daily Volatility",
  annualizedVolatility: "Annualized Volatility",
  maxDrawdown: "Max. Drawdown",
  indexResources: "INDEX RESOURCES",
  factsheet: "Factsheet",
  methodologyGuidebook: "Methodology Guidebook",
  constituentsList: "Constituents List",
  corporateActions: "Corporate Actions",
  benchmarkStatement: "Benchmark Statement",
  indexHistory: "Index History",
  rebalanceConstituents: "Rebalance Constituents",
  headerHome: "Home",
  headerIndexing: "Indexing",
  headerBitaIndexes: "BITA Indexes",
  headerIndexServices: "Index Services",
  headerBitacore: "BITACore",
  headerThematics: "Thematics",
  headerEsgImpact: "ESG & Impact",
  headerIndexingApis: "Indexing APIs",
  headerMore: "More",
  headerGovernance: "Governance",
  headerAnnouncements: "Announcements",
  headerBlog: "Blog",
  headerContactUs: "Contact Us",
  footerContactUsTitle: "Contact Us",
  footerContactUsDescription: "Contact us for more information and updates, as well as for any inquiries regarding access to our platform and services.",
  footerFollowUs: "Follow Us!",
  footerCopyright: "Copyright",
  footerCompany: "Company",
  footerAboutBita: "About BITA",
  footerOurClients: "Our Clients",
  footerGovernance: "Governance",
  footerCareers: "Careers",
  footerIndexing: "Indexing",
  footerBitaIndexes: "BITA Indexes",
  footerIndexLab: "Index Lab",
  footerBitacore: "BITACore",
  footerIndexServices: "Index Services",
  footerIndexingApis: "Indexing APIs",
  footerCryptoOffering: "Crypto Offering",
  footerIndexAnnouncements: "Index Announcements",
  footerData: "Data",
  footerThematics: "Thematics",
  footerEsgImpact: "ESG & Impact",
  footerConnect: "Connect",
  footerBlog: "Blog",
  footerLinkedin: "Linkedin",
  footerTwitter: "Twitter",
  footerContactUs: "Contact Us",
  footerNewsletter: "Newsletter",
  footerLegalPrivacy: "Legal & Privacy",
  footerCookiesPolicy: "Cookies Policy",
  footerTermsConditions: "Terms & Conditions",
  footerDisclaimer: "Disclaimer",
  footerImprint: "Imprint",
  footerPrivacyPolicy: "Privacy Policy",
  footerLicenseIndex: "License this Index",
  footerEnhanceProduct: "Enhance your product development!",
  footerLicenseBestInClass: "License best-in-class indexes!",
  footerUnlockPotential: "Unlock the potential of future trends!",
  footerGetStarted: "Get started!",
  footerContactUsTech: "Contact Us!",
  footerReadyToGetStarted: "Ready to get started?",
  footerLicenseIndexDescription: "In case you want to include this instrument in your product portfolio, please contact us, one of our Sales representatives will get back to you with more licensing details.",
  footerEnhanceProductDescription: "Experience the world's most powerful indexing construction platform! Contact our Sales team to discover our licensing pricing.",
  footerLicenseBestInClassDescription: "Contact us for more information and updates, as well as for any inquiries regarding accessing to our innovative indexes.",
  footerUnlockPotentialDescription: "Contact us for more information and updates, as well as for any inquiries regarding accessing to thematic landscape.",
  footerGetStartedDescription: "If you want to receive more information and updates, as well contact us for any inquiries regarding access to our platform and services:",
  footerContactUsTechDescription: "Contact us for more information and updates, as well as for any inquiries regarding access to our technology infrastructure.",
  footerReadyToGetStartedDescription: "Whether you're seeking more information, interested in our latest news and exciting updates, or have inquiries about access to BITACore, we're here for you.",
  breadcrumbBitaIndexes: "BITA Indexes",
  breadcrumbEquity: "Equity",
  breadcrumbFixedIncome: "Fixed Income",
  breadcrumbCommodities: "Commodities",
  breadcrumbThematic: "Thematic",
  breadcrumbSector: "Sector",
  breadcrumbESG: "ESG",
};

// French translations
const frTranslations: Record<TranslationKey, string> = {
  indexDescription: "Description de l'indice",
  performance: "Performance",
  from: "De",
  to: "À",
  top10Constituents: "Top 10 des Constituants",
  downloadMaterials: "Télécharger les Documents",
  utcDisclaimer: "*Veuillez noter que le graphique utilise l'heure UTC au lieu de l'heure locale.",
  ytd: "YTD",
  year1: "1 An",
  year3: "3 Ans",
  year5: "5 Ans",
  max: "Max.",
  indexSymbol: "Symbole de l'indice",
  currency: "Devise",
  figi: "FIGI",
  isin: "ISIN",
  nConstituents: "N° de Constituants",
  inceptionDate: "Date de Création",
  value: "Valeur",
  date: "Date",
  datePlaceholder: "aaaa-mm-jj",
  lastDayReturn: "Rendement du Dernier Jour",
  name: "NOM",
  ticker: "TICKER",
  indexShares: "PARTS D'INDICE",
  indexReturns: "Rendements de l'Indice",
  indexVolatility: "Volatilité de l'Indice",
  return: "Rendement",
  annualizedReturn: "Rendement Annualisé",
  sharpeRatio: "Ratio de Sharpe",
  dailyVolatility: "Volatilité Quotidienne",
  annualizedVolatility: "Volatilité Annualisée",
  maxDrawdown: "Perte Maximale",
  indexResources: "RESSOURCES DE L'INDICE",
  factsheet: "Fiche d'Information",
  methodologyGuidebook: "Guide Méthodologique",
  constituentsList: "Liste des Constituants",
  corporateActions: "Actions Corporatives",
  benchmarkStatement: "Déclaration de Référence",
  indexHistory: "Historique de l'Indice",
  rebalanceConstituents: "Rééquilibrer les Constituants",
  headerHome: "Accueil",
  headerIndexing: "Indexation",
  headerBitaIndexes: "Indices BITA",
  headerIndexServices: "Services d’index",
  headerBitacore: "BITACore",
  headerThematics: "Thématiques",
  headerEsgImpact: "ESG et Impact",
  headerIndexingApis: "API d’indexation",
  headerMore: "Plus",
  headerGovernance: "Gouvernance",
  headerAnnouncements: "Annonces",
  headerBlog: "Blog",
  headerContactUs: "Contactez-nous",
  footerContactUsTitle: "Contactez-nous",
  footerContactUsDescription: "Contactez-nous pour plus d'informations et de mises à jour, ainsi que pour toute demande concernant l'accès à notre plateforme et à nos services.",
  footerFollowUs: "Suivez-nous !",
  footerCopyright: "Droits d'auteur",
  footerCompany: "Entreprise",
  footerAboutBita: "À propos de BITA",
  footerOurClients: "Nos Clients",
  footerGovernance: "Gouvernance",
  footerCareers: "Carrières",
  footerIndexing: "Indexation",
  footerBitaIndexes: "Indices BITA",
  footerIndexLab: "Index Lab",
  footerBitacore: "BITACore",
  footerIndexServices: "Services d'index",
  footerIndexingApis: "API d'indexation",
  footerCryptoOffering: "Offre Crypto",
  footerIndexAnnouncements: "Annonces d'Index",
  footerData: "Données",
  footerThematics: "Thématiques",
  footerEsgImpact: "ESG et Impact",
  footerConnect: "Connecter",
  footerBlog: "Blog",
  footerLinkedin: "Linkedin",
  footerTwitter: "Twitter",
  footerContactUs: "Contactez-nous",
  footerNewsletter: "Newsletter",
  footerLegalPrivacy: "Légal et Confidentialité",
  footerCookiesPolicy: "Politique des Cookies",
  footerTermsConditions: "Termes et Conditions",
  footerDisclaimer: "Avertissement",
  footerImprint: "Mentions Légales",
  footerPrivacyPolicy: "Politique de Confidentialité",
  footerLicenseIndex: "Licencez cet Indice",
  footerEnhanceProduct: "Améliorez le développement de votre produit !",
  footerLicenseBestInClass: "Licencez les meilleurs indices !",
  footerUnlockPotential: "Libérez le potentiel des tendances futures !",
  footerGetStarted: "Commencer !",
  footerContactUsTech: "Contactez-nous !",
  footerReadyToGetStarted: "Prêt à commencer ?",
  footerLicenseIndexDescription: "Si vous souhaitez inclure cet instrument dans votre portefeuille de produits, veuillez nous contacter, l'un de nos représentants commerciaux vous répondra avec plus de détails sur les licences.",
  footerEnhanceProductDescription: "Découvrez la plateforme de construction d'indices la plus puissante au monde ! Contactez notre équipe commerciale pour découvrir nos tarifs de licence.",
  footerLicenseBestInClassDescription: "Contactez-nous pour plus d'informations et de mises à jour, ainsi que pour toute demande concernant l'accès à nos indices innovants.",
  footerUnlockPotentialDescription: "Contactez-nous pour plus d'informations et de mises à jour, ainsi que pour toute demande concernant l'accès au paysage thématique.",
  footerGetStartedDescription: "Si vous souhaitez recevoir plus d'informations et de mises à jour, contactez-nous pour toute demande concernant l'accès à notre plateforme et à nos services :",
  footerContactUsTechDescription: "Contactez-nous pour plus d'informations et de mises à jour, ainsi que pour toute demande concernant l'accès à notre infrastructure technologique.",
  footerReadyToGetStartedDescription: "Que vous recherchiez plus d'informations, que vous soyez intéressé par nos dernières actualités et mises à jour passionnantes, ou que vous ayez des questions sur l'accès à BITACore, nous sommes là pour vous.",
  breadcrumbBitaIndexes: "Indices BITA",
  breadcrumbEquity: "Actions",
  breadcrumbFixedIncome: "Revenu Fixe",
  breadcrumbCommodities: "Matières Premières",
  breadcrumbThematic: "Thématique",
  breadcrumbSector: "Secteur",
  breadcrumbESG: "ESG",
};

const translations: Record<LanguageCode, Record<TranslationKey, string>> = {
  [ISO_CODE_EN]: enTranslations,
  [ISO_CODE_FR]: frTranslations,
};

/**
 * Get translation for a given key and language
 * Falls back to English if translation not found
 */
export const getTranslation = (
  key: TranslationKey,
  language: LanguageCode = ISO_CODE_EN
): string => {
  const langTranslations = translations[language] || translations.en;
  return langTranslations[key] || translations.en[key] || key;
};

/**
 * Get all translations for a given language
 */
export const getTranslations = (
  language: LanguageCode = ISO_CODE_EN
): Record<TranslationKey, string> => {
  return translations[language] || translations.en;
};
